import React, { useState, useEffect } from 'react';
import { Bell, CheckCheck, BellOff } from 'lucide-react';
import { notificationsAPI } from '../lib/api';
import { LoadingPage } from '../components/shared';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const TYPE_CONFIG = {
  application: { color: 'var(--teal)', bg: 'rgba(45,212,191,0.1)' },
  sponsorship: { color: 'var(--gold)', bg: 'rgba(201,162,39,0.1)' },
  milestone: { color: 'var(--info)', bg: 'rgba(59,130,246,0.1)' },
  system: { color: 'var(--text-muted)', bg: 'rgba(255,255,255,0.05)' },
};

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = () => {
    notificationsAPI.getAll()
      .then(r => setNotifications(r.data))
      .catch(() => toast.error('Failed to load notifications'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const markAllRead = async () => {
    await notificationsAPI.markAllRead();
    setNotifications(n => n.map(x => ({ ...x, is_read: true })));
  };

  const handleClick = async (notif) => {
    if (!notif.is_read) {
      await notificationsAPI.markRead(notif.id);
      setNotifications(n => n.map(x => x.id === notif.id ? { ...x, is_read: true } : x));
    }
    if (notif.link) navigate(notif.link);
  };

  if (loading) return <LoadingPage />;

  const unread = notifications.filter(n => !n.is_read).length;

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Notifications</h1>
          <p className="page-subtitle">{unread} unread notification{unread !== 1 ? 's' : ''}</p>
        </div>
        {unread > 0 && (
          <button className="btn btn-secondary" onClick={markAllRead}>
            <CheckCheck size={16} /> Mark all read
          </button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--text-muted)' }}>
          <BellOff size={48} style={{ opacity: 0.3, margin: '0 auto 16px', display: 'block' }} />
          <div>No notifications yet</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {notifications.map(n => {
            const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.system;
            return (
              <div
                key={n.id}
                onClick={() => handleClick(n)}
                style={{
                  padding: '16px 20px',
                  background: n.is_read ? 'rgba(255,255,255,0.02)' : cfg.bg,
                  borderRadius: 10,
                  border: n.is_read ? 'var(--border-subtle)' : `1px solid ${cfg.color}30`,
                  cursor: n.link ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'flex-start', gap: 14, transition: 'all 0.15s'
                }}
              >
                {!n.is_read && (
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.color, flexShrink: 0, marginTop: 6 }} />
                )}
                <div style={{ flex: 1, marginLeft: n.is_read ? 22 : 0 }}>
                  <div style={{ fontWeight: n.is_read ? 400 : 600, color: 'var(--text-primary)', marginBottom: 4 }}>
                    {n.title}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: 6 }}>
                    {n.message}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                    {new Date(n.created_at).toLocaleString(undefined, {
                      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
